/**
 * BoardFullscreen2D — competitor-parity layout. Mirrors the
 * three-zone-plus-rail structure surfaced by the wondersccg.online
 * client (see "wonders front end.pdf"):
 *
 *   Top bar: deck/abyss counts · opponent fanned hand · score · round/turn
 *   Middle: full-bleed play area, the existing 7-realm Board recolored
 *   Bottom bar: mirror counts · Kingdom · Hoard · Energy · Actions · Draw · Pass · hand
 *   Right rail: vertical R1–R7 OPP/YOU pip column
 *
 * Keeps the same PublicTableState shape so switching modes mid-match is
 * a no-op for the underlying state; only the chrome around the board
 * changes.
 */

import { useDraggable, useDroppable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import { Coins, Target, Zap } from 'lucide-react';
import { type ReactElement, type ReactNode, useState } from 'react';
import type { CardData, PlayerId, PublicTableState, RealmName, TableAction } from '../types.js';
import { Board } from './Board.js';
import { BoardModeSwitcher } from './BoardModeSwitcher.js';
import { Card } from './Card.js';
// CardTooltip is wired into Card.tsx itself now; keep the import out of
// BoardFullscreen2D so we don't pull the dependency twice.
import { ZonePeek } from './ZonePeek.js';

type ZonePeekState =
  | { kind: 'deck'; cardIds: readonly string[] }
  | { kind: 'abyss'; cardIds: readonly string[] }
  | { kind: 'banished'; cardIds: readonly string[] };

type Props = {
  state: PublicTableState;
  viewer: PlayerId;
  opponent: PlayerId;
  cards: ReadonlyMap<string, CardData>;
  imageUrl: (cardId: string) => string;
  onSeizeStone: (idx: number, player: PlayerId | null) => void;
  dispatchAction: (a: TableAction) => void;
  events?: readonly unknown[];
};

const ROUNDS = 7;

// Stable keys for the opponent-hand placeholder strip — opp cards are
// face-down and have no per-slot identity, but React still needs unique
// keys. Using a precomputed array of strings keeps the render stable.
const OPP_HAND_KEYS = Array.from({ length: 30 }, (_, i) => `opp-hand-slot-${i}`);

export function BoardFullscreen2D({
  state,
  viewer,
  opponent,
  cards,
  imageUrl,
  onSeizeStone,
  dispatchAction,
  events = [],
}: Props): ReactElement {
  const oppZones = state.zones[opponent];
  const myZones = state.zones[viewer];
  const oppHandSize =
    oppZones && 'hand' in oppZones ? (oppZones.hand as readonly string[]).length : 0;
  const oppDeck = oppZones && 'deck' in oppZones ? (oppZones.deck as readonly string[]).length : 0;
  const oppAbyss =
    oppZones && 'abyss' in oppZones ? (oppZones.abyss as readonly string[]).length : 0;
  const myHand = myZones && 'hand' in myZones ? (myZones.hand as readonly string[]) : [];
  const myDeckCards =
    myZones && 'deck' in myZones ? (myZones.deck as readonly string[]) : ([] as readonly string[]);
  const myDeck = myDeckCards.length;
  const myAbyssCards =
    myZones && 'abyss' in myZones
      ? (myZones.abyss as readonly string[])
      : ([] as readonly string[]);
  const myAbyss = myAbyssCards.length;
  const myBanishedCards =
    myZones && 'banished' in myZones
      ? (myZones.banished as readonly string[])
      : ([] as readonly string[]);
  const myBanished = myBanishedCards.length;
  const myKingdomSize =
    myZones && 'kingdom' in myZones && Array.isArray(myZones.kingdom) ? myZones.kingdom.length : 0;
  const [peek, setPeek] = useState<ZonePeekState | null>(null);

  const myResources = state.resources?.[viewer] ?? { energy: 0, actions: 0, hoard: 0 };
  // Treat round=0 as round=1 for lane-locking purposes. Pre-fix matches
  // (created before sandbox startingRound was bumped to 1) persisted
  // with round=0 in their snapshots, which would lock every lane and
  // make the table unplayable until reset.
  const round = Math.max(1, state.round ?? 0);
  const isMyTurn = state.activePlayer === viewer;

  const oppPips = state.roundPips?.[opponent] ?? Array(ROUNDS).fill(null);
  const myPips = state.roundPips?.[viewer] ?? Array(ROUNDS).fill(null);
  const oppScore = oppPips.reduce<number>((a, x) => a + (x === 1 ? 1 : 0), 0);
  const myScore = myPips.reduce<number>((a, x) => a + (x === 1 ? 1 : 0), 0);

  return (
    <div className="flex h-screen w-screen flex-col bg-cosmos-950 text-cosmos-100">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="flex items-center gap-3 border-cosmos-800 border-b bg-cosmos-900/80 px-3 py-2">
        <div className="flex flex-col items-start text-[11px] text-cosmos-400 leading-tight">
          <span className="font-mono">{oppDeck} DECK</span>
          <span className="font-mono">{oppAbyss} ABYSS</span>
        </div>
        <div className="flex-1 text-center">
          <div className="text-[10px] uppercase tracking-wide text-cosmos-500">
            OPP {oppScore} — YOU {myScore}
          </div>
          <div className="mt-1 flex justify-center gap-0.5">
            {OPP_HAND_KEYS.slice(0, oppHandSize).map((k) => (
              <div
                key={k}
                className="h-3 w-2 rounded-sm bg-gradient-to-br from-cyan-700 to-cyan-900 border border-cyan-600/40"
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
        <div className="text-right text-xs text-amber-300">
          <div className="font-mono">
            Round {round}/{ROUNDS}
          </div>
          <div className={isMyTurn ? 'font-semibold text-amber-200' : 'text-cosmos-500'}>
            {isMyTurn ? 'Your Turn' : "Opponent's turn"}
          </div>
        </div>
        <BoardModeSwitcher />
      </header>

      {/* ── Middle play area ────────────────────────────────────── */}
      <main className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto bg-gradient-to-b from-cosmos-950 via-cosmos-900 to-cosmos-950 p-3">
          <Board
            realms={state.realms}
            viewer={viewer}
            opponent={opponent}
            cards={cards}
            imageUrl={imageUrl}
            currentRound={round}
            onPlacementContextMenu={() => undefined}
            onSeizeStone={onSeizeStone}
          />
        </div>

        {/* Game log — every TableActionApplied event rendered as a
            human-readable line. Replaces the R1–R7 pip rail; rounds
            are now tracked by the realm lanes themselves (closed →
            open as the round advances). */}
        <GameLogPanel events={events} viewer={viewer} cards={cards} />
      </main>

      {/* ── Bottom bar ──────────────────────────────────────────── */}
      <footer className="flex items-center gap-3 border-cosmos-800 border-t bg-cosmos-900/80 px-3 py-4">
        {/* Visible deck pile — stack of card-backs scaled by deck count.
            Click to peek (face-down — count only, per rules). */}
        <ZoneStack
          label="DECK"
          count={myDeck}
          tone="amber"
          tooltip="Drag to draw the top card. Drop on top to put cards back. Click to count."
          onClick={() => setPeek({ kind: 'deck', cardIds: myDeckCards })}
          droppableId="fs-zone-deck-top"
          droppableData={{ kind: 'deck', owner: viewer, position: 'top' }}
          {...(myDeckCards[0]
            ? {
                draggableId: `fs-pile-drag-deck-${myDeckCards[0]}`,
                dragData: {
                  cardId: myDeckCards[0],
                  source: { kind: 'deck', owner: viewer },
                },
              }
            : {})}
        />
        <ZoneStack
          label="BURY"
          count={myDeck}
          tone="amber"
          tooltip="Drop here to bury at the bottom of your deck"
          droppableId="fs-zone-deck-bottom"
          droppableData={{ kind: 'deck', owner: viewer, position: 'bottom' }}
        />
        {/* Visible abyss pile — public, click to expand. */}
        <ZoneStack
          label="ABYSS"
          count={myAbyss}
          tone="rose"
          tooltip="Drag to retrieve the top discard. Drop to discard. Click to view."
          onClick={() => setPeek({ kind: 'abyss', cardIds: myAbyssCards })}
          droppableId="fs-zone-abyss"
          droppableData={{ kind: 'abyss', owner: viewer, position: 'top' }}
          {...(myAbyssCards[0]
            ? {
                draggableId: `fs-pile-drag-abyss-${myAbyssCards[0]}`,
                dragData: {
                  cardId: myAbyssCards[0],
                  source: { kind: 'abyss', owner: viewer },
                },
              }
            : {})}
        />
        {/* Banishment pile — public, removed-from-game cards. */}
        <ZoneStack
          label="BANISHMENT"
          count={myBanished}
          tone="violet"
          tooltip="Drag to retrieve top of banishment. Drop to banish. Click to view."
          onClick={() => setPeek({ kind: 'banished', cardIds: myBanishedCards })}
          droppableId="fs-zone-banished"
          droppableData={{ kind: 'banished', owner: viewer, position: 'top' }}
          {...(myBanishedCards[0]
            ? {
                draggableId: `fs-pile-drag-banished-${myBanishedCards[0]}`,
                dragData: {
                  cardId: myBanishedCards[0],
                  source: { kind: 'banished', owner: viewer },
                },
              }
            : {})}
        />
        <div className="flex flex-col items-start text-[11px] leading-tight">
          <span className="text-cosmos-400">
            Kingdom: <span className="text-cosmos-100">{myKingdomSize || '—'}</span>
          </span>
        </div>
        <ResourcePill
          icon={<Zap className="h-3.5 w-3.5 text-amber-300" />}
          value={myResources.energy}
          label="Energy"
          onAdjust={(delta) =>
            dispatchAction({ kind: 'AdjustResource', resource: 'energy', delta })
          }
        />
        <ResourcePill
          icon={<Target className="h-3.5 w-3.5 text-rose-300" />}
          value={myResources.actions}
          label="Actions"
          onAdjust={(delta) =>
            dispatchAction({ kind: 'AdjustResource', resource: 'actions', delta })
          }
        />
        <ResourcePill
          icon={<Coins className="h-3.5 w-3.5 text-violet-300" />}
          value={myResources.hoard}
          label="Hoard"
          onAdjust={(delta) => dispatchAction({ kind: 'AdjustResource', resource: 'hoard', delta })}
        />

        <div className="flex flex-1 justify-center gap-2 overflow-x-auto px-2">
          {myHand.map((id, idx) => (
            <Card
              // biome-ignore lint/suspicious/noArrayIndexKey: hand may hold duplicate card_ids; idx disambiguates.
              key={`hand-${id}-${idx}`}
              cardId={id}
              card={cards.get(id)}
              imageUrl={imageUrl(id)}
              draggableId={`hand-${viewer}-${id}-${idx}`}
              dragData={{ cardId: id, source: { kind: 'hand', owner: viewer } }}
              size="md"
            />
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              // Draw — pop card from deck → hand. In sandbox, dispatch a MoveCard.
              const top = state.zones[viewer]
                ? (state.zones[viewer] as { deck?: readonly string[] }).deck?.[0]
                : undefined;
              if (top) {
                dispatchAction({
                  kind: 'MoveCard',
                  cardId: top,
                  from: { kind: 'deck', owner: viewer },
                  to: { kind: 'hand', owner: viewer },
                });
              }
            }}
            disabled={myDeck === 0}
            className="rounded bg-violet-700 px-3 py-1 text-xs font-medium text-white hover:bg-violet-600 disabled:opacity-40"
          >
            Draw
          </button>
          {(() => {
            const opp = state.players.find((p) => p !== viewer) ?? null;
            const pending = state.pendingUndo ?? null;
            const last = state.lastActor ?? null;
            const pendingByMe = pending?.requestedBy === viewer;
            const pendingByOpp = !!pending && !pendingByMe;
            const label = pendingByMe ? 'Undo requested…' : pendingByOpp ? 'Confirm undo' : 'Undo';
            const title = pendingByMe
              ? 'Click to cancel your undo request'
              : pendingByOpp
                ? "Confirm opponent's undo: rolls back both sides' last actions"
                : last === viewer
                  ? 'Roll back your last action and restore actions/energy'
                  : last === opp
                    ? 'Request undo — opponent must confirm by also clicking Undo'
                    : 'Nothing to undo';
            const disabled = !pending && !last;
            return (
              <button
                type="button"
                onClick={() => dispatchAction({ kind: 'Undo' })}
                disabled={disabled}
                title={title}
                className={
                  pendingByOpp
                    ? 'rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-40'
                    : pendingByMe
                      ? 'rounded border border-amber-700 px-3 py-1 text-xs text-amber-200 hover:bg-amber-900/30 disabled:opacity-40'
                      : 'rounded border border-cosmos-700 px-3 py-1 text-xs hover:bg-cosmos-800 disabled:opacity-40'
                }
              >
                {label}
              </button>
            );
          })()}
          <button
            type="button"
            onClick={() => dispatchAction({ kind: 'AnnotateMove', note: 'Pass' })}
            className="rounded border border-cosmos-700 px-3 py-1 text-xs hover:bg-cosmos-800"
          >
            Pass
          </button>
          <button
            type="button"
            onClick={() => dispatchAction({ kind: 'AdvanceRound' })}
            disabled={round >= ROUNDS}
            className="rounded bg-amber-700 px-3 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-40"
            title="Advance to the next round (opens the next realm lane)."
          >
            End Round
          </button>
        </div>
      </footer>

      {peek ? (
        <ZonePeek
          title={
            peek.kind === 'deck'
              ? 'Your Deck'
              : peek.kind === 'abyss'
                ? 'Your Abyss'
                : 'Your Banishment'
          }
          cardIds={peek.cardIds}
          visibility={peek.kind === 'deck' ? 'private' : 'public'}
          cards={cards}
          imageUrl={imageUrl}
          onClose={() => setPeek(null)}
          {...(peek.kind === 'abyss' || peek.kind === 'banished'
            ? { source: { zone: peek.kind, owner: viewer } }
            : {})}
        />
      ) : null}
    </div>
  );
}

// ─── Game log ───────────────────────────────────────────────────────
//
// Renders every TableActionApplied event as a single line so the
// player can see opponent moves, costs paid, and card abilities used.
// Replaces the R1–R7 pip rail; the rounds are now expressed as the
// realm lanes themselves (closed → open as the round advances).

type ActionEvent = {
  kind: string;
  payload?: { actor?: string; action?: { kind: string; [k: string]: unknown } };
  actor?: string;
  at?: number;
};

function describeAction(
  a: { kind: string; [k: string]: unknown } | undefined,
  cards: ReadonlyMap<string, CardData>,
): string {
  if (!a) return '';
  const name = (id: unknown): string => {
    if (typeof id !== 'string') return String(id ?? '');
    return cards.get(id)?.name ?? id;
  };
  switch (a.kind) {
    case 'MoveCard': {
      const cardId = a.cardId as string;
      const from = a.from as { kind: string; owner?: string; realm?: string } | undefined;
      const to = a.to as { kind: string; owner?: string; realm?: string } | undefined;
      // Drawing (own deck → own hand) is a private move: the card
      // identity stays hidden from the opponent (and from the public
      // log) until it's played. Render a name-free line instead.
      if (
        from?.kind === 'deck' &&
        to?.kind === 'hand' &&
        from.owner !== undefined &&
        from.owner === to.owner
      ) {
        return 'drew a card';
      }
      const fromTxt = from?.realm ? from.realm : (from?.kind ?? '?');
      const toTxt = to?.realm ? to.realm : (to?.kind ?? '?');
      return `played ${name(cardId)} (${fromTxt} → ${toTxt})`;
    }
    case 'TapCard':
      return `tapped ${name(a.cardId)}`;
    case 'UntapCard':
      return `untapped ${name(a.cardId)}`;
    case 'FlipCard':
      return `flipped ${name(a.cardId)}`;
    case 'AddCounter':
      return `+${a.n as number} ${a.counter as string} on ${name(a.cardId)}`;
    case 'RemoveCounter':
      return `−${a.n as number} ${a.counter as string} on ${name(a.cardId)}`;
    case 'MoveStone':
      return `seized stone in ${a.realm as string}`;
    case 'RollDice':
      return `rolled d${a.sides as number} = ${a.result as number}`;
    case 'ChatMessage':
      return `chat: "${(a.text as string).slice(0, 60)}"`;
    case 'AdjustResource':
      return `${(a.delta as number) > 0 ? '+' : ''}${a.delta as number} ${a.resource as string}`;
    case 'AdvanceRound':
      return 'ended round';
    case 'Mulligan':
      return `mulliganed ${(a.cardIds as readonly string[]).length} card(s)`;
    case 'DeclareVictory':
      return 'declared victory';
    case 'ConcedeMatch':
      return 'conceded';
    case 'EmoteReaction':
      return `emote ${a.emoji as string}`;
    case 'AnnotateMove':
      return `note: ${(a.note as string).slice(0, 60)}`;
    case 'Undo':
      return 'undid last action';
    default:
      return a.kind;
  }
}

export function GameLogPanel({
  events,
  viewer,
  cards,
}: {
  events: readonly unknown[];
  viewer: PlayerId;
  cards: ReadonlyMap<string, CardData>;
}): ReactElement {
  const lines: Array<{ at: number; side: 'YOU' | 'OPP'; text: string }> = [];
  for (const raw of events) {
    const e = raw as ActionEvent;
    if (e.kind !== 'TableActionApplied' || !e.payload?.action) continue;
    const actor = e.payload.actor ?? e.actor;
    const side: 'YOU' | 'OPP' = actor === viewer ? 'YOU' : 'OPP';
    const text = describeAction(e.payload.action, cards);
    if (text) lines.push({ at: e.at ?? 0, side, text });
  }
  return (
    <aside className="flex w-72 flex-col border-cosmos-800 border-l bg-cosmos-900/60 px-3 py-2">
      <h3 className="mb-2 font-mono text-[11px] uppercase tracking-wide text-cosmos-400">
        Game Log
      </h3>
      <ul className="flex-1 space-y-1 overflow-y-auto font-mono text-[11px]">
        {lines.length === 0 ? (
          <li className="italic text-cosmos-600">no moves yet</li>
        ) : (
          lines
            .slice()
            .reverse()
            .map((l, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: log lines share at-timestamps; idx disambiguates.
                key={`${l.at}-${i}`}
                className={l.side === 'YOU' ? 'text-amber-200' : 'text-rose-300'}
              >
                <span className="opacity-60">{l.side}</span> {l.text}
              </li>
            ))
        )}
      </ul>
    </aside>
  );
}

export function ZoneStack({
  label,
  count,
  tone,
  tooltip,
  onClick,
  droppableId,
  droppableData,
  draggableId,
  dragData,
}: {
  label: string;
  count: number;
  tone: 'amber' | 'rose' | 'violet';
  tooltip: string;
  onClick?: () => void;
  /** When set, the stack registers as a dnd-kit drop target with the
   *  supplied data payload (e.g. `{ kind: 'abyss', owner: viewer }`).
   *  The Table.tsx onDragEnd reads this to dispatch a MoveCard. */
  droppableId?: string;
  droppableData?: Record<string, unknown>;
  /** When set, the stack also acts as a drag source for its top card
   *  — letting the user pull `pile[0]` directly to hand / realm /
   *  another pile without opening the peek modal. The data payload is
   *  the same shape every other draggable uses:
   *  `{ cardId, source: { kind, owner } }`. */
  draggableId?: string;
  dragData?: Record<string, unknown>;
}): ReactElement {
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: droppableId ?? `zone-${label}-disabled`,
    data: droppableData ?? {},
    disabled: !droppableId,
  });
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: draggableId ?? `zone-${label}-drag-disabled`,
    data: dragData ?? {},
    disabled: !draggableId || count === 0,
  });
  // Compose the drag + drop refs onto the same DOM element. Order
  // doesn't matter — both setters store the node into their own
  // internal table.
  const setNodeRef = (node: HTMLElement | null): void => {
    setDropRef(node);
    setDragRef(node);
  };
  const dragStyle = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 }
    : undefined;
  const ring =
    tone === 'amber'
      ? 'ring-amber-700/60'
      : tone === 'rose'
        ? 'ring-rose-700/60'
        : 'ring-violet-700/60';
  const bg =
    tone === 'amber'
      ? 'from-amber-700 to-amber-950'
      : tone === 'rose'
        ? 'from-rose-800 to-rose-950'
        : 'from-violet-800 to-violet-950';
  const hoverRing =
    tone === 'amber'
      ? 'hover:ring-amber-300'
      : tone === 'rose'
        ? 'hover:ring-rose-300'
        : 'hover:ring-violet-300';
  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      style={dragStyle}
      {...listeners}
      {...attributes}
      className={`relative flex h-32 w-24 flex-shrink-0 flex-col items-center justify-end rounded-sm transition ${onClick ? `cursor-pointer ${hoverRing} hover:ring-2 hover:ring-offset-1 hover:ring-offset-cosmos-900` : 'cursor-default'} ${isOver ? 'ring-2 ring-offset-1 ring-offset-cosmos-900 ring-cyan-300' : ''} ${draggableId && count > 0 ? 'cursor-grab active:cursor-grabbing' : ''} ${isDragging ? 'opacity-60' : ''}`}
      title={tooltip}
    >
      {/* Stack effect — three slightly offset rectangles to suggest a pile.
          Empty piles render as a dashed outline so the position stays
          recognisable. */}
      {count > 0 ? (
        <>
          {count > 2 ? (
            <span
              className={`absolute inset-x-0 top-2 h-24 -rotate-2 rounded-sm bg-gradient-to-br ${bg} opacity-40 ring-1 ${ring}`}
            />
          ) : null}
          {count > 1 ? (
            <span
              className={`absolute inset-x-0 top-1 h-24 rotate-1 rounded-sm bg-gradient-to-br ${bg} opacity-70 ring-1 ${ring}`}
            />
          ) : null}
          <span
            className={`absolute inset-x-0 top-0 h-24 rounded-sm bg-gradient-to-br ${bg} ring-1 ${ring}`}
          />
        </>
      ) : (
        <span className="absolute inset-x-0 top-0 h-24 rounded-sm border border-cosmos-700 border-dashed" />
      )}
      <span
        className={`relative z-10 mt-auto rounded bg-cosmos-950/90 px-1.5 py-0.5 font-mono text-[11px] font-bold ${
          tone === 'amber'
            ? 'text-amber-200'
            : tone === 'rose'
              ? 'text-rose-200'
              : 'text-violet-200'
        }`}
      >
        {label} {count}
      </span>
    </button>
  );
}

export function ResourcePill({
  icon,
  value,
  label,
  onAdjust,
}: {
  /** Emoji string OR a ReactNode (e.g. a lucide-react SVG icon). The
   *  pill renders whatever's passed in inside an aria-hidden span. */
  icon: string | ReactNode;
  value: number;
  label: string;
  onAdjust?: (delta: number) => void;
}): ReactElement {
  return (
    <div
      className="flex items-center gap-1 rounded border border-cosmos-700 bg-cosmos-800/80 px-2 py-0.5 text-xs"
      title={label}
    >
      <span aria-hidden="true" className="flex items-center">
        {icon}
      </span>
      {/* Re-keying the motion span on `value` re-runs the enter
          animation each time the resource changes — gives a brief
          scale-pulse + colour flash that draws the eye to the change.
          Cheap reactivity without the boilerplate of a manual
          previous-value diff. */}
      <motion.span
        key={value}
        initial={{ scale: 1.55, color: '#fde68a' }}
        animate={{ scale: 1, color: 'inherit' }}
        transition={{ duration: 0.32, ease: 'easeOut' }}
        className="min-w-[1.2em] text-center font-mono"
      >
        {value}
      </motion.span>
      {onAdjust && (
        <span className="ml-1 flex flex-col leading-none">
          <button
            type="button"
            className="h-3 w-4 text-[10px] text-cosmos-300 hover:text-amber-300"
            onClick={() => onAdjust(1)}
            title={`${label} +1`}
          >
            ▲
          </button>
          <button
            type="button"
            className="h-3 w-4 text-[10px] text-cosmos-300 hover:text-amber-300"
            onClick={() => onAdjust(-1)}
            title={`${label} −1`}
          >
            ▼
          </button>
        </span>
      )}
    </div>
  );
}

// Suppress unused-realm-name warning until realm-typed lookups land.
void (null as RealmName | null);
